import './polyfills';

import React from 'react';
import { createRoot } from 'react-dom/client';
import { Provider } from 'react-redux';
import Spinner from '@atlaskit/spinner';

import ErrorBoundary, { UnexpectedApplicationErrorPage } from './components/ErrorBoundary';

import {
  addBreadcrumb,
  captureException,
  initClient as initErrorReportingClient,
  setAnonymousIdTag,
  setUserId as setErrorReportingUserId,
  initSentry,
} from './utilities/analytics/error-reporting';
import LocaleSwitcher from './components/LocaleSwitcher';
import {
  buildInitialState,
  createAnalyticsClient,
  startupAnalyticEvent,
  pushGlobals,
  preloadAppState,
} from './utilities/init-app-state';
import { initCastle } from './castle';
import AnalyticsClientProvider from './containers/AnalyticsClientProvider';
import { createAppRouter } from './containers/App';
import { startBrowserMetrics } from './browser-metrics';
import { createAppStore } from './stores/redux.store';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { FeatureGateProvider } from './providers/FeatureGateProvider';
import { AppRouterProvider } from './providers/AppRouterProvider';
import { getFfsId } from './utilities/ffs-id';
import AppProvider from './containers/AppProvider';
import FedrampUseIdProbe from './utilities/debug/FedrampUseIdProbe';
import { isFedM } from './utilities/env';
import { applyCobrandingBootTheme } from './utilities/cobranding/bootTheme';

// Apply the active cobranding's document theme before first paint to avoid a
// white flash during loading. No-op for experiences without a boot theme.
applyCobrandingBootTheme();

// Render a spinner to remove the "failed to load scripts" error while loading the app
const container = document.getElementById('root');

const root = createRoot(container!);

root.render(
  <div style={{ display: 'flex', height: '100%', justifyContent: 'center', alignItems: 'center' }}>
    <Spinner size="large" />
  </div>
);

const preloadedState = preloadAppState();
initSentry();

buildInitialState(preloadedState)
  .then(async initialState => {
    const { initialStoreState, cspNonce } = initialState;

    initErrorReportingClient(initialStoreState.microbranding);

    const analyticsClient = createAnalyticsClient(initialState);

    const anonymousId = analyticsClient.getAnonymousId();
    setErrorReportingUserId(anonymousId);
    setAnonymousIdTag(anonymousId);

    startupAnalyticEvent(analyticsClient);

    startBrowserMetrics(analyticsClient);

    const ffsId = getFfsId();
    if (ffsId) {
      analyticsClient.setFfsId(ffsId);
    }

    pushGlobals(initialStoreState.appConfig, cspNonce);

    const store = createAppStore(initialStoreState);

    if (!initialStoreState.appConfig) {
      addBreadcrumb({
        level: 'error',
        message: 'Unable to retrieve appConfig during buildInitialState',
      });
    }

    initCastle(initialStoreState.appConfig);

    const queryClient = new QueryClient({ defaultOptions: { queries: { retry: false } } });

    const router = createAppRouter();

    root.render(
      <Provider store={store}>
        <LocaleSwitcher>
          <ErrorBoundary analyticsClient={analyticsClient}>
            <AppProvider>
              <QueryClientProvider client={queryClient}>
                <AnalyticsClientProvider>
                  <FeatureGateProvider>
                    {isFedM() && <FedrampUseIdProbe context="root" />}
                    <AppRouterProvider router={router} />
                  </FeatureGateProvider>
                </AnalyticsClientProvider>
              </QueryClientProvider>
            </AppProvider>
          </ErrorBoundary>
        </LocaleSwitcher>
      </Provider>
    );
  })
  .catch(error => {
    captureException(error);

    const emptyStore = createAppStore({});
    root.render(
      <Provider store={emptyStore}>
        <LocaleSwitcher>
          <UnexpectedApplicationErrorPage />
        </LocaleSwitcher>
      </Provider>
    );
  });
